import React from 'react';

function renderChart(chartJson: string, key: number): React.ReactNode {
    try {
        const data = JSON.parse(chartJson);
        if (!data.data || !Array.isArray(data.data)) return null;

        const values = data.data.map((d: any) => Math.abs(Number(d.value) || 0));
        const maxVal = Math.max(...values, 1);

        return (
            <div key={key} className="ai-insight-chart">
                {data.title && (
                    <div className="ai-insight-chart__title">
                        {data.title}
                    </div>
                )}
                <div className="ai-insight-chart__bars">
                    {data.data.map((d: any, i: number) => {
                        const val = Number(d.value) || 0;
                        const pct = (Math.abs(val) / maxVal) * 100;
                        const isNegative = val < 0;
                        return (
                            <div key={i} className="ai-insight-chart__row">
                                <div className="ai-insight-chart__label">{d.label}</div>
                                <div className="ai-insight-chart__bar-track">
                                    <div
                                        className={`ai-insight-chart__bar-fill ${isNegative ? 'ai-insight-chart__bar-fill--negative' : ''}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                <div className="ai-insight-chart__value">
                                    {val.toLocaleString()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    } catch {
        return <div key={key} className="ai-insight-chart__error">Invalid chart data</div>;
    }
}

/**
 * A bare `http(s)://…` URL in plain text. Markdown links are matched by INLINE_TOKEN below.
 * Only http(s) targets are recognised, so a `javascript:` or `data:` URL stays inert text.
 */
const BARE_URL_PATTERN = /https?:\/\/[^\s<>"'`]+/gi;

/** Punctuation that ends a sentence rather than a URL ("see https://x.com/a."). */
const TRAILING_PUNCTUATION = /[.,;:!?'"*_]+$/;

/** Trims sentence punctuation and an unbalanced closing paren from a bare URL. */
function trimBareUrl(url: string): string {
    let out = url.replace(TRAILING_PUNCTUATION, '');
    // "(see https://x.com/a)" — drop the ")" unless the URL itself opened a paren.
    while (out.endsWith(')') && (out.match(/\(/g) || []).length < (out.match(/\)/g) || []).length) {
        out = out.slice(0, -1).replace(TRAILING_PUNCTUATION, '');
    }
    return out;
}

/** True for an absolute http(s) URL that the browser parses (rejects e.g. "https://"). */
function isSafeHttpUrl(url: string): boolean {
    try {
        const { protocol, hostname } = new URL(url);
        return (protocol === 'http:' || protocol === 'https:') && hostname.length > 0;
    } catch {
        return false;
    }
}

function renderLink(href: string, label: React.ReactNode, key: string): React.ReactNode {
    return (
        <a
            key={key}
            className="ai-insight-md__link"
            href={href}
            // Agent output is untrusted: open in a new tab and give the target no handle back
            // to this window, and send no referrer from inside the app.
            target="_blank"
            rel="noopener noreferrer"
        >
            {label}
        </a>
    );
}

/** Splits plain text into strings and clickable bare-URL links. */
function renderBareUrls(text: string, keyPrefix: string): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    let last = 0;
    let n = 0;
    // A fresh regex per call: the pattern is global, so a shared instance would carry
    // lastIndex across calls. (exec rather than matchAll — the TS lib target is es2019.)
    const re = new RegExp(BARE_URL_PATTERN.source, BARE_URL_PATTERN.flags);
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        const start = match.index;
        const href = trimBareUrl(match[0]);
        if (!isSafeHttpUrl(href)) {
            continue; // left as plain text; `last` stays put so it is emitted with the rest
        }
        if (start > last) out.push(text.slice(last, start));
        out.push(renderLink(href, href, `${keyPrefix}-a${n++}`));
        // The URL may have lost trailing punctuation; that is emitted as text after the link.
        last = start + href.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

/**
 * Inline tokens, matched left to right in one pass. At any position the alternatives are tried
 * in this order, which is what makes nesting unambiguous:
 *   1. `code`               — literal; nothing inside it is linked or emphasised
 *   2. [label](https://…)   — a link; the label may itself carry `code` or **bold**
 *   3. **bold**             — emphasis; its content is formatted again (so it may hold links)
 * Text between tokens is scanned for bare http(s) URLs.
 *
 * Splitting on one kind of marker before looking for the next (bold, then code, then links)
 * cuts `[**label**](url)` into "[", "**label**" and "](url)", so the link is never seen and
 * its brackets leak into the reply as text. One pass avoids that.
 *
 * The label pattern runs on untrusted agent output, so it must not backtrack. Its two parts are
 * mutually exclusive — a `code` span, or one character that is not a backtick, "]" or newline —
 * so every character has exactly one way to match. (When a backtick could also match as a plain
 * character, an unterminated "[``````…" line took exponential time: ~50ms at 45 characters,
 * ~45s at 81.) The cap bounds how far each "[" scans before giving up; real labels are short.
 * Cost: a label with a lone, unmatched backtick is not treated as a link (its URL still is).
 */
const MAX_LINK_LABEL_PARTS = 300;
const INLINE_TOKEN = new RegExp(
    [
        '(`[^`\\n]+`)',
        String.raw`\[((?:` + '`[^`\\n]*`' + String.raw`|[^` + '`' + String.raw`\]\n]){1,${MAX_LINK_LABEL_PARTS}})\]\((https?:\/\/[^\s)]+)\)`,
        String.raw`\*\*([^*]+)\*\*`,
    ].join('|'),
    'g'
);

/** A link label: `code` and **bold** are formatted, but nothing in it becomes a nested link. */
function renderLabel(label: string, keyPrefix: string): React.ReactNode[] {
    return label.split(/(`[^`\n]+`|\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) => {
        if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) {
            return <code key={`${keyPrefix}-l${i}`} className="ai-insight-inline-code">{part.slice(1, -1)}</code>;
        }
        if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) {
            return <strong key={`${keyPrefix}-l${i}`}>{part.slice(2, -2)}</strong>;
        }
        return part;
    });
}

function renderInlineTokens(text: string, keyPrefix: string): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    const re = new RegExp(INLINE_TOKEN.source, INLINE_TOKEN.flags);
    let last = 0;
    let n = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        const [whole, code, label, href, bold] = match;
        const key = `${keyPrefix}-t${n++}`;
        let node: React.ReactNode;
        if (code !== undefined) {
            // Code stays literal — a URL or link inside backticks is shown, not linked.
            node = <code key={key} className="ai-insight-inline-code">{code.slice(1, -1)}</code>;
        } else if (href !== undefined) {
            // An unsafe or malformed target stays as the original text, unlinked.
            node = isSafeHttpUrl(href)
                ? renderLink(href, renderLabel(label, key), key)
                : null;
        } else {
            node = <strong key={key}>{renderInlineTokens(bold, key)}</strong>;
        }
        if (node === null) {
            continue; // left in place; emitted with the surrounding text below
        }
        if (match.index > last) {
            out.push(...renderBareUrls(text.slice(last, match.index), `${key}-g`));
        }
        out.push(node);
        last = match.index + whole.length;
    }
    if (last < text.length) {
        out.push(...renderBareUrls(text.slice(last), `${keyPrefix}-end`));
    }
    return out;
}

function renderInline(text: string): React.ReactNode {
    return <>{renderInlineTokens(text, 'i')}</>;
}

function renderTable(rows: string[], startKey: string): React.ReactNode {
    // Strip only the optional leading/trailing pipe before splitting. Filtering out every
    // empty string instead would drop genuinely empty interior cells (`| a || c |`), shifting
    // the remaining columns left and misaligning body values against their headers.
    const parsed = rows.map(row =>
        row.trim()
            .replace(/^\|/, '')
            .replace(/\|$/, '')
            .split('|')
            .map(c => c.trim())
    );

    const separatorIdx = parsed.findIndex(cells =>
        cells.every(c => /^[-:\s]+$/.test(c))
    );

    const headerRows = separatorIdx > 0 ? parsed.slice(0, separatorIdx) : [];
    const bodyRows = separatorIdx >= 0 ? parsed.slice(separatorIdx + 1) : parsed;

    return (
        <div key={startKey} className="ai-insight-table__wrapper">
            <table className="ai-insight-table">
                {headerRows.length > 0 && (
                    <thead>
                        {headerRows.map((cells, ri) => (
                            <tr key={ri}>
                                {cells.map((cell, ci) => (
                                    <th key={ci}>{renderInline(cell)}</th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                )}
                <tbody>
                    {bodyRows.map((cells, ri) => (
                        <tr key={ri}>
                            {cells.map((cell, ci) => (
                                <td key={ci}>{renderInline(cell)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function renderMarkdown(text: string): React.ReactNode {
    // Extract chart blocks
    const chartBlocks: { placeholder: string; json: string }[] = [];
    let processedText = text.replace(/```chart\s*\n([\s\S]*?)\n```/g, (_, json) => {
        const placeholder = `__CHART_${chartBlocks.length}__`;
        chartBlocks.push({ placeholder, json: json.trim() });
        return placeholder;
    });

    // Extract code blocks (``` ... ```)
    const codeBlocks: { placeholder: string; content: string; lang: string }[] = [];
    processedText = processedText.replace(/```(\w*)\s*\n([\s\S]*?)\n```/g, (_, lang, content) => {
        const placeholder = `__CODE_${codeBlocks.length}__`;
        codeBlocks.push({ placeholder, content, lang: lang || '' });
        return placeholder;
    });

    const lines = processedText.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const trimmed = lines[i].trim();

        // Chart placeholder
        const chartMatch = trimmed.match(/^__CHART_(\d+)__$/);
        if (chartMatch) {
            const idx = parseInt(chartMatch[1], 10);
            const block = chartBlocks[idx];
            if (block) {
                elements.push(renderChart(block.json, i));
            }
            i++;
            continue;
        }

        // Code block placeholder
        const codeMatch = trimmed.match(/^__CODE_(\d+)__$/);
        if (codeMatch) {
            const idx = parseInt(codeMatch[1], 10);
            const block = codeBlocks[idx];
            if (block) {
                elements.push(
                    <pre key={i} className="ai-insight-code-block">
                        <code>{block.content}</code>
                    </pre>
                );
            }
            i++;
            continue;
        }

        // Table: collect consecutive pipe-delimited rows
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            const tableRows: string[] = [];
            while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                tableRows.push(lines[i].trim());
                i++;
            }
            // The while loop above already advanced `i` past the table, and this branch
            // continues without a further increment — so a bare `i` key would collide with
            // the element pushed for the first non-table line. Namespace it.
            elements.push(renderTable(tableRows, `table-${i}`));
            continue;
        }

        // Headers
        if (trimmed.startsWith('### ')) {
            elements.push(<h4 key={i} className="ai-insight-md__h4">{renderInline(trimmed.slice(4))}</h4>);
        } else if (trimmed.startsWith('## ')) {
            elements.push(<h3 key={i} className="ai-insight-md__h3">{renderInline(trimmed.slice(3))}</h3>);
        } else if (trimmed.startsWith('# ')) {
            elements.push(<h2 key={i} className="ai-insight-md__h2">{renderInline(trimmed.slice(2))}</h2>);
        }
        // Bullet points
        else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            elements.push(
                <div key={i} className="ai-insight-md__bullet">{renderInline(trimmed.slice(2))}</div>
            );
        }
        // Numbered lists
        else if (/^\d+\.\s/.test(trimmed)) {
            const content = trimmed.replace(/^\d+\.\s/, '');
            const num = trimmed.match(/^(\d+)\./)?.[1];
            elements.push(
                <div key={i} className="ai-insight-md__numbered"><span className="ai-insight-md__num">{num}.</span> {renderInline(content)}</div>
            );
        }
        // Empty line
        else if (trimmed === '') {
            elements.push(<div key={i} className="ai-insight-md__spacer" />);
        }
        // Regular text
        else {
            elements.push(<div key={i} className="ai-insight-md__text">{renderInline(trimmed)}</div>);
        }

        i++;
    }

    return <>{elements}</>;
}
